
import { Component, Prop, Vue, Watch } from 'nuxt-property-decorator'
import { Loader } from '@googlemaps/js-api-loader'
import { OnMounted } from '~/core/vue.types'
import {
  mapStyles,
  overlayMapStyleDetailed,
  overlayMapStylesSimplified,
} from '~/components/sections/coverage/map-styles'
import { TiledLayerMapType } from '~/components/sections/coverage/layers/TiledLayerMapType'
import { BaseMapOverlayType, NetworkName, NetworkType, TiledLayer } from '~/components/sections/coverage/types'

@Component
export default class CoverageMapCanvas extends Vue implements OnMounted {
  @Prop({ required: true, default: '', type: String })
  readonly providerNetwork!: NetworkName

  @Prop({ required: true, default: '', type: String })
  readonly networkType!: NetworkType

  @Prop({ required: true, default: '', type: String })
  readonly countryCode!: string

  @Prop({ required: true, default: () => ({}), type: Object })
  readonly place!: google.maps.places.PlaceResult

  @Prop({ required: false, default: null, type: Object })
  readonly currentLocation!: GeolocationCoordinates | null

  private isMapReady = false

  private googleAPI!: typeof window.google

  private S3BucketPathMap = [
    { name: 'salt', type: '3G', path: '3G' },
    { name: 'salt', type: '4G', path: '4G' },
    { name: 'salt', type: '4G+', path: '4Gplus' },
    { name: 'salt', type: '5G', path: '5G' },
    { name: 'salt', type: '5G+', path: '5Gplus' },

    { name: 'sunrise', type: '4G', path: '4G' },
    { name: 'sunrise', type: '5G', path: '5G' },
    { name: 'sunrise', type: '5G+', path: '5Gplus' },

    { name: 'swisscom', type: '3G', path: '3G' },
    { name: 'swisscom', type: '4G', path: '4G' },
    { name: 'swisscom', type: '4G+', path: '4Gplus' },
    { name: 'swisscom', type: '5G', path: '5G' },
    { name: 'swisscom', type: '5G+', path: '5Gplus' },
  ]

  private layers: TiledLayer[] = []

  private currentBaseMapLayerType: BaseMapOverlayType = 'simplified'

  private isTiledLayerFirstAttach = true

  private map!: google.maps.Map
  private markers: google.maps.Marker[] = []

  setMapReady () {
    this.isMapReady = true
  }

  private createMap () {
    this.map = new this.googleAPI.maps.Map(this.$refs.coverageMap as HTMLElement, {
      zoom: 8,
      minZoom: 8,
      maxZoom: 16,
      center: new this.googleAPI.maps.LatLng(46.900492, 8.117529),
      styles: mapStyles,
      mapTypeId: 'blankbase',
      mapTypeControl: false,
      streetViewControl: false,
      clickableIcons: false,
      scaleControl: true,
      zoomControlOptions: {
        position: this.googleAPI.maps.ControlPosition.LEFT_BOTTOM,
      },
      fullscreenControlOptions: {
        position: this.googleAPI.maps.ControlPosition.RIGHT_BOTTOM,
      },
    })
  }

  addEventListeners (eventName: string, cb: () => void) {
    this.googleAPI.maps.event.addListener(this.map, eventName, cb)
  }

  private getTiledLayerInstance (networkName: string, networkType: string, path: string): TiledLayer {
    const tiledLayerInstance = this.layers.find(l => l.name === networkName && l.type === networkType)

    if (!tiledLayerInstance) {
      return this.createTiledLayerInstance(networkName, networkType, path)
    }

    return tiledLayerInstance
  }

  private createTiledLayerInstance (networkName: string, networkType: string, path: string): TiledLayer {
    const tileSize = new this.googleAPI.maps.Size(256, 256)
    const layerName = `${networkName}_${networkType}`
    const tilesVersion = this.$config.COVERAGE_MAP_TILES_VERSION
    const layer: TiledLayer = {
      name: networkName,
      type: networkType,
      instance: new TiledLayerMapType(networkName, tilesVersion, layerName, path, tileSize).getInstance(),
    }

    this.layers.push(layer)

    return layer
  }

  public clearMarkers (): void {
    this.markers.forEach(marker => marker.setMap(null))
    this.markers = []
  }

  public fitBoundsToLocation (location: google.maps.LatLng) {
    const bounds = new this.googleAPI.maps.LatLngBounds()
    bounds.extend(location)

    this.map.fitBounds(bounds)
  }

  public addMarker (title: string | undefined, position: google.maps.LatLng): void {
    const marker = new this.googleAPI.maps.Marker({
      map: this.map,
      icon: {
        scaledSize: new this.googleAPI.maps.Size(46, 46),
        url: 'https://maps.google.com/mapfiles/kml/paddle/red-circle.png',
      },
      title,
      position,
    })

    this.markers.push(marker)
  }

  initMap () {
    this.createMap()
    this.addEventListeners('zoom_changed', this.onZoomChangedHandler.bind(this))
    this.setMapTiledLayer(this.providerNetwork, this.networkType)
  }

  private onZoomChangedHandler () {
    const zoom = this.map.getZoom()

    if (!zoom) {
      return
    }

    if (zoom < 14 && this.currentBaseMapLayerType !== 'simplified') {
      this.setBaseMapLayerType('simplified')
    }

    if (zoom >= 14 && this.currentBaseMapLayerType !== 'detailed') {
      this.setBaseMapLayerType('detailed')
    }
  }

  private setMapTiledLayer (networkName: NetworkName, networkType: NetworkType) {
    const path = this.S3BucketPathMap.find(n => n.name === networkName && n.type === networkType)?.path || ''

    this.attachTiledLayer(networkName, networkType, path)
  }

  private attachTiledLayer (networkName: string, networkType: string, path: string) {
    const { instance: tiledLaterInstance } = this.getTiledLayerInstance(networkName, networkType, path)

    this.setTiledMapLayerType(tiledLaterInstance)

    if (this.isTiledLayerFirstAttach) {
      this.setBaseMapLayerType('simplified')
      this.isTiledLayerFirstAttach = false
    }
  }

  private setTiledMapLayerType (tiledLaterInstance: google.maps.ImageMapType) {
    this.map.overlayMapTypes.removeAt(0)

    // TODO(@justlstn): check and fix types
    // TODO: (@justlstn) check and fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.map.overlayMapTypes.insertAt(0, tiledLaterInstance)
  }

  private getStyledMapType (type: BaseMapOverlayType) {
    switch (type) {
      case 'simplified':
        return new this.googleAPI.maps.StyledMapType(overlayMapStylesSimplified)
      case 'detailed':
        return new this.googleAPI.maps.StyledMapType(overlayMapStyleDetailed)
    }
  }

  private setBaseMapLayerType (overlayType: BaseMapOverlayType) {
    const styledMapOverlayType = this.getStyledMapType(overlayType)

    this.map.overlayMapTypes.removeAt(1)

    // TODO(@justlstn): check and fix types
    // TODO: (@justlstn) check and fix types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.map.overlayMapTypes.insertAt(1, styledMapOverlayType)

    this.currentBaseMapLayerType = overlayType
  }

  async mounted () {
    const loader = new Loader({
      apiKey: this.$config.GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    })

    this.googleAPI = await loader.load()

    this.initMap()
    this.setMapReady()
  }

  @Watch('place')
  private async onPlaceChanged (place: google.maps.places.PlaceResult): Promise<void> {
    const { name: address } = place

    const isPlaceWithZipCode = place.types?.includes('postal_code')

    this.clearMarkers()

    if (isPlaceWithZipCode) {
      try {
        const results = await this.geocode({ address })

        if (results) {
          const { geometry: { location }, formatted_address: formattedAddress } = results[0]

          this.map.setCenter(location)
          this.addMarker(formattedAddress, location)
        }
      } catch (e) {
        // TODO: Handle error?
        console.error(e)
      }
    } else if (place.geometry?.location) {
      this.addMarker(address, place.geometry.location)
      this.fitBoundsToLocation(place.geometry.location)
    }
  }

  geocode (
    options: Omit<google.maps.GeocoderRequest, 'componentRestrictions'> & {
      componentRestrictions?: google.maps.GeocoderComponentRestrictions,
    }) {
    const geocoder = new this.googleAPI.maps.Geocoder()

    options.componentRestrictions = options.componentRestrictions || {
      country: this.countryCode,
    }

    return new Promise<google.maps.GeocoderResult[] | null>((resolve, reject) => {
      geocoder.geocode(
        options,
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === this.googleAPI.maps.GeocoderStatus.OK) {
            return resolve(results)
          } else {
            return reject(new Error('Unable to geocode location'))
          }
        })
    })
  }

  @Watch('providerNetwork')
  @Watch('networkType')
  onNetworkNameOrTypeChanged () {
    this.setMapTiledLayer(this.providerNetwork, this.networkType)
  }

  @Watch('currentLocation')
  onCurrentLocationChange (position: GeolocationCoordinates) {
    const location = new this.googleAPI.maps.LatLng(position.latitude, position.longitude)

    this.map.setCenter(location)
    this.fitBoundsToLocation(location)
    this.addMarker('', location)
  }
}
